/**
 * Оплата звёздами: счёт → окно Telegram → ожидание зачисления.
 *
 * Окно оплаты сообщает «paid» в момент списания, а срок зачисляет бот,
 * получив successful_payment, — на это уходит от доли секунды до
 * нескольких секунд. Поэтому после «paid» платёж опрашивается, пока
 * сервер не подтвердит зачисление: показать «готово» раньше значит
 * показать подписку со старой датой.
 */
import { hapticFeedback, invoice } from '@telegram-apps/sdk-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError, apiGet, apiPost } from '@/shared/api/client';
import {
  paymentStatusSchema,
  starsInvoiceSchema,
  type PaymentStatus,
} from '@/shared/api/schemas';

export interface PayRequest {
  plan_code: string;
  period_code: string;
  extend_subscription_id: string | null;
}

export type PayPhase = 'idle' | 'creating' | 'paying' | 'confirming';

/** Итог оплаты. `payment: null` — оплачено, но зачисление ещё не подтвердилось. */
export interface PayResult {
  payment: PaymentStatus | null;
}

const POLL_INTERVAL_MS = 1200;
const POLL_ATTEMPTS = 20;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function waitForCredit(paymentId: string): Promise<PaymentStatus | null> {
  for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt += 1) {
    try {
      const payment = await apiGet(`/payments/${paymentId}`, paymentStatusSchema);
      if (payment.status === 'succeeded') return payment;
    } catch {
      // Сеть мигнула — звёзды уже списаны, поэтому не сдаёмся, а ждём дальше.
    }
    await sleep(POLL_INTERVAL_MS);
  }
  return null;
}

function messageOf(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isUnauthorized) return 'Сессия устарела. Закройте и откройте приложение заново.';
    return error.detail;
  }
  return 'Не удалось связаться с сервером. Попробуйте ещё раз.';
}

export function usePayStars() {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<PayPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PayResult | null>(null);

  /** Возвращает true, когда звёзды списаны (даже если зачисление ещё в пути). */
  const pay = async (request: PayRequest): Promise<boolean> => {
    if (phase !== 'idle') return false;
    setError(null);

    if (!invoice.open.isAvailable()) {
      setError('Оплата звёздами работает только внутри Telegram.');
      return false;
    }

    setPhase('creating');
    try {
      const created = await apiPost('/payments/stars', request, starsInvoiceSchema);

      setPhase('paying');
      const status = await invoice.open(created.invoice_link, 'url');
      if (status === 'cancelled') return false;
      if (status === 'failed') {
        setError('Оплата не прошла. Звёзды не списаны.');
        return false;
      }

      // 'paid' и 'pending' одинаково ведут к ожиданию: во втором случае
      // Telegram ещё проводит списание, и итог придёт боту.
      setPhase('confirming');
      const payment = await waitForCredit(created.payment_id);
      await queryClient.invalidateQueries({ queryKey: ['subscription'] });
      if (hapticFeedback.notificationOccurred.isAvailable()) {
        hapticFeedback.notificationOccurred('success');
      }
      setResult({ payment });
      return true;
    } catch (caught) {
      setError(messageOf(caught));
      return false;
    } finally {
      setPhase('idle');
    }
  };

  return {
    pay,
    phase,
    error,
    result,
    reset: () => {
      setResult(null);
      setError(null);
    },
  };
}
